import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const SIZE = 132;
const STROKE = 14;
const RADIUS = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

function LegendRow({ color, label, value }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: color }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>{value}</Typography>
    </Stack>
  );
}
LegendRow.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired
};

/**
 * Donut SVG (sin dependencias) "Tu progreso – Últimos 30 días": segmentos
 * Completadas/Programadas/Ausencias + % de asistencia al centro + link a progreso completo.
 */
function SessionProgressDonut({ completed = 0, scheduled = 0, absent = 0, onViewMore = null }) {
  const theme = useTheme();
  const { t } = useTranslation(['sessions']);

  const colors = {
    completed: theme.palette.sessionProximity.near,
    scheduled: theme.palette.sessionStatus.scheduled.fg,
    absent: theme.palette.sessionStatus.absent.fg
  };

  const total = completed + scheduled + absent;
  const attendancePct = completed + absent > 0
    ? Math.round((completed / (completed + absent)) * 100)
    : 0;

  // Segmentos del anillo (en orden completed → scheduled → absent).
  const segments = useMemo(() => {
    if (total === 0) return [];
    const order = [
      { key: 'completed', value: completed, color: colors.completed },
      { key: 'scheduled', value: scheduled, color: colors.scheduled },
      { key: 'absent', value: absent, color: colors.absent }
    ];
    let offset = 0;
    return order
      .filter((s) => s.value > 0)
      .map((s) => {
        const length = (s.value / total) * CIRC;
        const seg = { ...s, dasharray: `${length} ${CIRC - length}`, dashoffset: -offset };
        offset += length;
        return seg;
      });
  }, [completed, scheduled, absent, total, colors.completed, colors.scheduled, colors.absent]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
        {t('progressLast30', { ns: 'sessions', defaultValue: 'Tu progreso – Últimos 30 días' })}
      </Typography>
      <Stack direction="row" spacing={2.5} alignItems="center">
        <Box sx={{ position: 'relative', width: SIZE, height: SIZE, flexShrink: 0 }}>
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={theme.palette.hairline.strong}
              strokeWidth={STROKE}
            />
            {segments.map((s) => (
              <circle
                key={s.key}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke={s.color}
                strokeWidth={STROKE}
                strokeDasharray={s.dasharray}
                strokeDashoffset={s.dashoffset}
                strokeLinecap="round"
                transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
              />
            ))}
          </svg>
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>{attendancePct}%</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('attendance', { ns: 'sessions', defaultValue: 'Asistencia' })}
            </Typography>
          </Box>
        </Box>
        <Stack spacing={1} sx={{ flexGrow: 1 }}>
          <LegendRow color={colors.completed} label={t('statusCompletedPlural', { ns: 'sessions', defaultValue: 'Completadas' })} value={completed} />
          <LegendRow color={colors.scheduled} label={t('statusScheduledPlural', { ns: 'sessions', defaultValue: 'Programadas' })} value={scheduled} />
          <LegendRow color={colors.absent} label={t('absences', { ns: 'sessions', defaultValue: 'Ausencias' })} value={absent} />
        </Stack>
      </Stack>
      {onViewMore && (
        <Button
          variant="text"
          color="inherit"
          onClick={onViewMore}
          sx={{ mt: 1.5, p: 0, color: 'secondary.main', fontWeight: 700, fontSize: '0.82rem', textTransform: 'none', '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
        >
          {t('viewFullProgress', { ns: 'sessions', defaultValue: 'Ver mi progreso completo' })}
        </Button>
      )}
    </Box>
  );
}

SessionProgressDonut.propTypes = {
  completed: PropTypes.number,
  scheduled: PropTypes.number,
  absent: PropTypes.number,
  onViewMore: PropTypes.func
};

export default SessionProgressDonut;
